/**
 * Reload the page when the Firestore client has permanently failed.
 *
 * WebKit (iOS/macOS Safari) can drop or corrupt its IndexedDB connection,
 * typically after the app has been in the background. Firestore only
 * retries its own IndexedDbTransactionError; any other IndexedDB error
 * (e.g. "UnknownError: Attempt to iterate a cursor that doesn't exist")
 * fails its internal AsyncQueue for good. From then on every operation
 * throws "INTERNAL ASSERTION FAILED (ID: b815)" or never settles, so the
 * app shows loading forever. A reload is the only way back. Offline writes
 * already queued in IndexedDB survive it, and the PWA service worker
 * serves the app shell when there's no connection.
 *
 * Detection: the SDK logs "INTERNAL UNHANDLED ERROR" when the queue fails.
 * Most of the resulting errors are swallowed by listeners and callers, so
 * that log is the only reliable signal. We watch console.error instead of
 * using `onLog()` from firebase/app because the app and firestore packages
 * bundle separate copies of @firebase/logger, so `onLog()` never sees
 * Firestore's logs.
 *
 * Reloads are rate-limited per tab so a device whose IndexedDB stays
 * broken can't reload in a loop.
 */

import * as Sentry from '@sentry/react'

const LAST_RELOAD_KEY = 'firestore-recovery-last-reload'
const MIN_RELOAD_INTERVAL_MS = 5 * 60_000
const SENTRY_FLUSH_TIMEOUT_MS = 2000
// Written by the cache-mode fallback this module replaces (MCP-643).
const LEGACY_CACHE_MODE_KEY = 'firestore-cache-mode'
const FIRESTORE_LOGGER = '@firebase/firestore'
const FATAL_SIGNATURES = [
  'INTERNAL UNHANDLED ERROR',
  'INTERNAL ASSERTION FAILED',
]

let installed = false
let reloading = false
let reportedSkippedReload = false
let originalConsoleError: typeof console.error | null = null

/** Whether console.error args are Firestore reporting a failed client. */
export function isFatalFirestoreLog(args: unknown[]): boolean {
  const text = args
    .map((arg) => (arg instanceof Error ? arg.message : String(arg)))
    .join(' ')
  return (
    text.includes(FIRESTORE_LOGGER) &&
    FATAL_SIGNATURES.some((signature) => text.includes(signature))
  )
}

function readLastReload(): number | null {
  try {
    const value = sessionStorage.getItem(LAST_RELOAD_KEY)
    return value ? Number(value) : null
  } catch {
    return null
  }
}

function writeLastReload(time: number): void {
  try {
    sessionStorage.setItem(LAST_RELOAD_KEY, String(time))
  } catch {
    // Storage unavailable; the in-memory `reloading` flag still stops
    // repeat reloads within this page load.
  }
}

function recover(logMessage: string): void {
  const now = Date.now()
  const lastReload = readLastReload()

  // Too soon after the last recovery reload: skip it, but a dead client
  // keeps logging on every operation, so a later log re-checks and reloads
  // once the interval has passed. Report the skip only once.
  if (lastReload !== null && now - lastReload < MIN_RELOAD_INTERVAL_MS) {
    if (!reportedSkippedReload) {
      reportedSkippedReload = true
      Sentry.captureMessage(
        'Firestore client failed again soon after a recovery reload; not reloading',
        {
          level: 'error',
          extra: { logMessage, msSinceLastReload: now - lastReload },
        },
      )
    }
    return
  }

  reloading = true
  Sentry.captureMessage('Firestore client failed; reloading', {
    level: 'error',
    extra: { logMessage },
  })
  writeLastReload(now)
  // Give Sentry a moment to send the event before the page goes away.
  void Sentry.flush(SENTRY_FLUSH_TIMEOUT_MS)
    .catch(() => false)
    .then(() => window.location.reload())
}

/**
 * Watch for Firestore reporting a failed client and reload the page.
 * Call once at startup, before Firestore is used.
 */
export function installFirestoreRecovery(): void {
  if (installed) return
  installed = true

  try {
    localStorage.removeItem(LEGACY_CACHE_MODE_KEY)
  } catch {
    // Storage unavailable; nothing to clean up.
  }

  const consoleError = console.error
  originalConsoleError = consoleError
  console.error = (...args: unknown[]) => {
    consoleError.apply(console, args)
    if (!reloading && isFatalFirestoreLog(args)) {
      recover(args.map(String).join(' '))
    }
  }
}

/** Test-only: restore console.error and reset module state. */
export function __resetForTests(): void {
  if (originalConsoleError) console.error = originalConsoleError
  originalConsoleError = null
  installed = false
  reloading = false
  reportedSkippedReload = false
}
